import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicsCard from '@/components/feature/ComicsCard';
import { mangaCategories, mangaList } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = mangaCategories.find((c) => c.slug === slug) || mangaCategories[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const filteredManga =
    slug === 'all' || !slug
      ? mangaList
      : mangaList.filter((m) => m.categorySlug === slug);

  const categoryTitle = currentCategory.name === '全部' ? '全部漫画' : `${currentCategory.name}漫画`;

  const [selectedTab, setSelectedTab] = useState(slug || 'all');

  const handleTabClick = (catSlug: string) => {
    setSelectedTab(catSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const newUrl = `/category/${catSlug}`;
    window.history.pushState(null, '', newUrl);

    const activeCat = mangaCategories.find((c) => c.slug === catSlug) || mangaCategories[0];
    document.title = `${activeCat.name === '全部' ? '全部漫画' : activeCat.name + '漫画'} - 韩国漫画网`;
  };

  const displayManga =
    selectedTab === 'all'
      ? mangaList
      : mangaList.filter((m) => m.categorySlug === selectedTab);

  return (
    <>
      <SEOHead
        title={`${categoryTitle} - 韩国漫画网`}
        description={`韩国漫画网${categoryTitle}专区，提供最新最全的${categoryTitle}免费在线阅读，无删减下拉式韩漫大全，完整内容原汁原味。`}
        keywords={`${categoryTitle},韩漫在线阅读,免费韩国漫画,无删减,下拉式大全`}
        canonicalUrl={`https://koreanmanga.example.com/category/${slug}`}
        schemaData={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${categoryTitle} - 韩国漫画网`,
            description: `${categoryTitle}免费在线阅读专区`,
            url: `https://koreanmanga.example.com/category/${slug}`,
          },
        ]}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: categoryTitle },
            ]}
          />

          <div className="mb-10">
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900">
              {categoryTitle}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              {slug === 'all'
                ? '浏览全部韩国漫画作品，发现你喜欢的韩漫'
                : `浏览${currentCategory.name}类韩国漫画，共 ${filteredManga.length} 部作品`}
            </p>
          </div>

          <div className="flex items-center flex-wrap gap-2 mb-8">
            {mangaCategories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleTabClick(cat.slug)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  selectedTab === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                  <i className={cat.icon}></i>
                </span>
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-20">
          {displayManga.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 md:gap-6">
              {displayManga.map((manga) => (
                <ComicsCard key={manga.id} manga={manga} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto flex items-center justify-center text-foreground-200">
                <i className="ri-inbox-line text-5xl"></i>
              </div>
              <p className="text-foreground-500 mt-4">该分类下暂无漫画，请浏览其他分类</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}